import { Core } from "@faire/design-tokens";
import { trackInsiderLoggedOutMarketingPageTestimonialsView } from "@faire/web-api/events/insider/view/loggedOutMarketingPageTestimonials";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveProp } from "@faire/web/slate/Theme/ResponsiveProp";
import { TypographyVariants } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { type JSX } from "react";
import Slider from "react-slick";
import styled, { css } from "styled-components";

// eslint-disable-next-line import/no-unassigned-import
import "@faire/retailer/components/slick.css";

import { EyebrowText } from "./common";
import { TestimonialCard } from "./TestimonialsCard";
import { ITestimonialsData } from "./TestinomialsData";

interface IProps {
  testimonials: readonly ITestimonialsData[];
  title: JSX.Element;
  backgroundColor: string;
  titleColor?: string;
  textStyle?: ResponsiveProp<TypographyVariants>;
  textColor: string;
}

export const TestimonialsCardSection: React.FC<IProps> = ({
  testimonials,
  title,
  backgroundColor,
  textColor,
  textStyle,
  titleColor,
}) => {
  const viewport = useViewport();
  const sliderRef = React.useRef<Slider>(null);
  const ref = useDoActionWhenVisible(() => {
    trackInsiderLoggedOutMarketingPageTestimonialsView();
  });

  return (
    <Section $withVerticalPadding $backgroundColor={backgroundColor} ref={ref}>
      {viewport.isMobile ? (
        <HorizontalPaddingWrapper>
          <EyebrowText $color={titleColor ? titleColor : textColor}>
            {title}
          </EyebrowText>
          <Spacer height="4x" />
        </HorizontalPaddingWrapper>
      ) : null}
      <Slider
        dots
        arrows={false}
        autoplay
        autoplaySpeed={5000}
        infinite
        pauseOnHover
        ref={sliderRef}
      >
        {testimonials.map((testimonial) => (
          <HorizontalPaddingWrapper key={testimonial.name}>
            <TestimonialCard
              testimonial={testimonial}
              title={title}
              textColor={textColor}
              textStyle={textStyle}
              titleColor={titleColor}
            />
          </HorizontalPaddingWrapper>
        ))}
      </Slider>
      {viewport.isTabletAndAbove && testimonials.length > 1 ? (
        <ArrowsWrapper>
          <ArrowComponent
            direction="left"
            onClick={() => sliderRef.current?.slickPrev()}
          />
          <ArrowComponent
            direction="right"
            onClick={() => sliderRef.current?.slickNext()}
          />
        </ArrowsWrapper>
      ) : null}
    </Section>
  );
};

interface IArrowProps {
  direction: "left" | "right";
  onClick: () => void;
}

const ArrowComponent = ({ direction, onClick }: IArrowProps) => {
  const marginValue = useResponsiveValue({
    mobileAndAbove: 0,
    tabletAndAbove: -40,
    desktopAndAbove: -60,
    xLargeAndAbove: -70,
  });
  return (
    <ArrowWrapper
      onClick={onClick}
      $arrowDirection={direction}
      $marginValue={marginValue}
      align="center"
      justify="center"
    >
      <Arrow direction={direction} />
    </ArrowWrapper>
  );
};

const ArrowWrapper = styled(Row)<{
  $arrowDirection: "left" | "right";
  $marginValue: number;
}>`
  pointer-events: auto;
  border: 1px solid ${Core.border.muted};
  background-color: ${Core.surface.inverse};
  width: 35px;
  height: 35px;
  border-radius: 35px;
  ${responsiveMediaQueries.mobile} {
    ${(props) => css`margin-${props.$arrowDirection}: 0px`}
  }

  ${responsiveMediaQueries.tablet} {
    ${(props) => css`margin-${props.$arrowDirection}: -50px`}
  }

  ${responsiveMediaQueries.desktop} {
    ${(props) => css`margin-${props.$arrowDirection}: -70px`}
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    ${(props) => css`margin-${props.$arrowDirection}: -80px`}
  }
`;

const horizontalPadding = css`
  padding-left: 32px;
  padding-right: 32px;

  ${responsiveMediaQueries.tablet} {
    padding-left: 72px;
    padding-right: 72px;
  }

  ${responsiveMediaQueries.desktop} {
    padding-left: 128px;
    padding-right: 128px;
  }

  ${responsiveMediaQueries.xlarge} {
    padding-left: 208px;
    padding-right: 208px;
  }

  ${responsiveMediaQueries.xxlarge} {
    padding-left: 184px;
    padding-right: 184px;
  }
`;

const verticalPadding = css`
  padding-top: 48px;
  padding-bottom: 48px;

  ${responsiveMediaQueries.tablet} {
    padding-top: 72px;
    padding-bottom: 72px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding-top: 128px;
    padding-bottom: 128px;
  }
`;

const Section = styled(Column).attrs({ as: "section" })<{
  $backgroundColor?: string;
  $withHorizontalPadding?: boolean;
  $withVerticalPadding?: boolean;
}>`
  position: relative;
  ${(props) =>
    props.$backgroundColor
      ? css`
          background-color: ${props.$backgroundColor};
        `
      : ""}

  ${(props) => (props.$withHorizontalPadding ? horizontalPadding : "")}
  ${(props) => (props.$withVerticalPadding ? verticalPadding : "")}
`;

const HorizontalPaddingWrapper = styled.div`
  ${horizontalPadding}
`;

const ArrowsWrapper = styled(Row).attrs({ justify: "space-between" })`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  pointer-events: none;
  ${horizontalPadding}
`;
